import { useEffect, useState, useMemo } from "react";
import { Link, useOutletContext, useParams } from "react-router-dom";
import api from "@/api/client";
import { ChevronRight, Clock, ThumbsUp, ThumbsDown, Copy, Twitter, Linkedin, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import { editorJsToHtml } from "@/components/EditorJsRenderer";
import { articleT, applyArticleLang } from "@/lib/publicI18n";

export default function PublicArticlePage() {
  const { articleSlug } = useParams();
  const { space, slug, basePath = `/s/${slug}`, lang } = useOutletContext();
  const t = articleT(lang);
  const langCode = lang || "en";
  const dateLocale = langCode === "ar" ? "ar" : "en-US";
  const [data, setData] = useState(null);
  const [feedback, setFeedback] = useState(null);
  const [feedbackMsg, setFeedbackMsg] = useState("");
  const [feedbackSent, setFeedbackSent] = useState(false);

  useEffect(() => {
    setData(null);
    api.get(`/public/articles/${articleSlug}?space=${encodeURIComponent(slug)}&lang=${langCode}`).then((r) => {
      setData({
        ...r.data,
        article: applyArticleLang(r.data.article, langCode),
        related: (r.data.related || []).map((a) => applyArticleLang(a, langCode)),
      });
    });
    setFeedback(null);
    setFeedbackSent(false);
    setFeedbackMsg("");
  }, [slug, articleSlug, langCode]);

  const contentHtml = useMemo(() => editorJsToHtml(data?.article?.content), [data]);

  const toc = useMemo(() => {
    if (!contentHtml) return [];
    const div = document.createElement("div");
    div.innerHTML = contentHtml;
    return Array.from(div.querySelectorAll("h2, h3")).map((h, i) => {
      const id = h.textContent.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "") + "-" + i;
      return { id, level: h.tagName, text: h.textContent };
    });
  }, [contentHtml]);

  const renderedHtml = useMemo(() => {
    if (!contentHtml) return "";
    const div = document.createElement("div");
    div.innerHTML = contentHtml;
    Array.from(div.querySelectorAll("h2, h3")).forEach((h, i) => {
      const id = h.textContent.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "") + "-" + i;
      h.id = id;
    });
    return div.innerHTML;
  }, [contentHtml]);

  const submitFeedback = async (type) => {
    setFeedback(type);
    if (type === "helpful") {
      try {
        await api.post(`/public/feedback?space=${encodeURIComponent(slug)}`, {
          article_id: data.article.id, type,
          visitor_id: localStorage.getItem("visitor_id") || "",
        });
        setFeedbackSent(true);
        toast.success(t.thanks);
      } catch {}
    }
  };

  const submitNotHelpful = async () => {
    try {
      await api.post(`/public/feedback?space=${encodeURIComponent(slug)}`, {
        article_id: data.article.id, type: "not_helpful",
        message: feedbackMsg,
        visitor_id: localStorage.getItem("visitor_id") || "",
      });
      setFeedbackSent(true);
      toast.success(t.thanksImprove);
    } catch {}
  };

  if (!data) return <div className="p-12 text-center text-zinc-500">{t.loading}</div>;
  const { article, related } = data;

  const shareUrl = typeof window !== "undefined" ? window.location.href : "";

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 py-10 grid grid-cols-1 lg:grid-cols-4 gap-8 fade-in">
      <article className="lg:col-span-3 min-w-0">
        <nav className="text-xs text-zinc-500 mb-4 flex items-center gap-1 flex-wrap">
          <Link to={basePath || "/"} className="hover:underline">{t.home}</Link>
          <ChevronRight className="w-3 h-3 rtl:rotate-180" />
          {article.category && (
            <>
              <Link to={`${basePath}/c/${article.category.slug}`} className="hover:underline">{article.category.name}</Link>
              <ChevronRight className="w-3 h-3 rtl:rotate-180" />
            </>
          )}
          <span className="truncate">{article.title}</span>
        </nav>

        <header className="mb-6 pb-6 border-b-2 border-zinc-900">
          <h1 className="text-3xl sm:text-5xl font-black tracking-tighter mb-3 uppercase"
            style={{ fontFamily: "var(--theme-font-heading)" }} data-testid="article-title">
            {article.title}
          </h1>
          <div className="flex items-center gap-4 text-sm text-zinc-500 flex-wrap">
            <div className="flex items-center gap-2">
              <div className="w-7 h-7 rounded-full bg-zinc-200 flex items-center justify-center text-xs font-bold">
                {article.author?.full_name?.charAt(0) || "?"}
              </div>
              <span>{article.author?.full_name || t.anonymous}</span>
            </div>
            <span className="flex items-center gap-1">
              <Clock className="w-3.5 h-3.5" /> {article.reading_time_minutes} {t.minRead}
            </span>
            <span>{t.updated} {new Date(article.updated_at).toLocaleDateString(dateLocale)}</span>
          </div>
        </header>

        {article.featured_image_url && (
          <img src={article.featured_image_url} alt={article.title} className="w-full rounded-sm mb-6 border border-zinc-200" />
        )}

        <div className="prose-doc" dangerouslySetInnerHTML={{ __html: renderedHtml }} data-testid="article-content" />

        {space.settings?.show_ratings !== false && (
          <div className="mt-12 pt-8 border-t border-zinc-200">
            {feedbackSent ? (
              <div className="text-center text-zinc-600" data-testid="feedback-thanks">{t.thanks}</div>
            ) : feedback === "not_helpful" ? (
              <div data-testid="feedback-not-helpful-form">
                <div className="text-sm font-bold mb-2">{t.feedbackMsg}</div>
                <textarea value={feedbackMsg} onChange={(e) => setFeedbackMsg(e.target.value)} rows={3}
                  placeholder={t.feedbackPlaceholder}
                  className="w-full px-3 py-2 border border-zinc-300 rounded-sm text-sm focus:outline-none focus:ring-2"
                  style={{ "--tw-ring-color": "var(--theme-primary)" }} />
                <button onClick={submitNotHelpful}
                  className="mt-2 text-white font-semibold text-sm px-4 py-2 rounded-sm"
                  style={{ background: "var(--theme-primary)" }}>{t.sendFeedback}</button>
              </div>
            ) : (
              <div className="text-center">
                <div className="text-sm font-bold mb-3">{t.helpfulQ}</div>
                <div className="flex justify-center gap-2">
                  <button onClick={() => submitFeedback("helpful")}
                    data-testid="feedback-helpful"
                    className="border border-zinc-300 hover:bg-emerald-50 hover:border-emerald-300 px-5 py-2 rounded-sm text-sm font-semibold flex items-center gap-2">
                    <ThumbsUp className="w-4 h-4" /> {t.yes}
                  </button>
                  <button onClick={() => submitFeedback("not_helpful")}
                    data-testid="feedback-not-helpful"
                    className="border border-zinc-300 hover:bg-rose-50 hover:border-rose-300 px-5 py-2 rounded-sm text-sm font-semibold flex items-center gap-2">
                    <ThumbsDown className="w-4 h-4" /> {t.no}
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        <div className="mt-8 pt-6 border-t border-zinc-200">
          <div className="text-xs uppercase tracking-wider font-bold text-zinc-500 mb-3">{t.share}</div>
          <div className="flex gap-2 flex-wrap">
            <button onClick={() => { navigator.clipboard.writeText(shareUrl); toast.success(t.linkCopied); }}
              className="border border-zinc-300 hover:bg-zinc-50 px-3 py-1.5 rounded-sm text-xs font-semibold flex items-center gap-1.5"
              data-testid="share-copy">
              <Copy className="w-3 h-3" /> {t.copyLink}
            </button>
            <a href={`https://twitter.com/intent/tweet?text=${encodeURIComponent(article.title)}&url=${encodeURIComponent(shareUrl)}`}
              target="_blank" rel="noopener noreferrer"
              className="border border-zinc-300 hover:bg-zinc-50 px-3 py-1.5 rounded-sm text-xs font-semibold flex items-center gap-1.5">
              <Twitter className="w-3 h-3" /> {t.tweet}
            </a>
            <a href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`}
              target="_blank" rel="noopener noreferrer"
              className="border border-zinc-300 hover:bg-zinc-50 px-3 py-1.5 rounded-sm text-xs font-semibold flex items-center gap-1.5">
              <Linkedin className="w-3 h-3" /> {t.shareLinkedin}
            </a>
          </div>
        </div>

        {related.length > 0 && (
          <div className="mt-10">
            <h3 className="text-xs uppercase tracking-wider font-bold text-zinc-500 mb-3">{t.related}</h3>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              {related.map((r) => (
                <Link key={r.slug} to={`${basePath}/a/${r.slug}`} className="border border-zinc-200 rounded-sm p-4 hover:border-zinc-400 transition-colors">
                  <div className="font-semibold text-sm mb-1 line-clamp-2" style={{ fontFamily: "var(--theme-font-heading)" }}>{r.title}</div>
                  <div className="text-xs text-zinc-500 line-clamp-2">{r.excerpt}</div>
                </Link>
              ))}
            </div>
          </div>
        )}

        {article.category && (
          <div className="mt-8">
            <Link to={`${basePath}/c/${article.category.slug}`}
              className="text-sm font-semibold flex items-center gap-1 hover:underline"
              style={{ color: "var(--theme-primary)" }}>
              <ArrowLeft className="w-4 h-4 rtl:rotate-180" /> {t.backTo} {article.category.name}
            </Link>
          </div>
        )}
      </article>

      {space.settings?.show_table_of_contents !== false && toc.length > 0 && (
        <aside className="hidden lg:block">
          <div className="sticky top-24">
            <div className="text-xs uppercase tracking-wider font-bold text-[var(--pp-text-dim)] mb-3">{t.onThisPage}</div>
            <nav className="space-y-1 text-sm border-l border-[var(--pp-border)] rtl:border-l-0 rtl:border-r">
              {toc.map((item) => (
                <a key={item.id} href={`#${item.id}`}
                  className={`block py-1 -ml-px rtl:ml-0 rtl:-mr-px border-l rtl:border-l-0 rtl:border-r border-transparent hover:border-[var(--pp-accent)] text-[var(--pp-text-muted)] hover:text-[var(--pp-text)] transition-colors ${item.level === "H3" ? "pl-6 rtl:pl-0 rtl:pr-6 text-xs" : "pl-3 rtl:pl-0 rtl:pr-3"}`}>
                  {item.text}
                </a>
              ))}
            </nav>
          </div>
        </aside>
      )}
    </div>
  );
}
